'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';

import { GalleryThumbnails } from '@eliadmin/core/src/components/dlz/product-detail/gallery/gallery-thumbnails';
import { collectGalleryImages } from '@eliadmin/core/src/components/dlz/product-detail/gallery/gallery-images';
import { Badge } from '@eliadmin/core/src/components/dlz/ui/badge';
import { IconButton } from '@eliadmin/core/src/components/dlz/ui/icon-button';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { resolveDzlGalleryImageAlt } from '@eliadmin/core/src/utils/dlz/product-image-alt';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';

const GalleryLightbox = lazy(() =>
  import('@eliadmin/core/src/components/dlz/product-detail/gallery/gallery-lightbox').then(
    module => ({ default: module.GalleryLightbox }),
  ),
);

const PLACEHOLDER = '/images/placeholder.png';
/** Same as the product card: sale badge first, at most two badges. */
const MAX_BADGES = 2;
const SLIDE = '[aria-roledescription="slide"]';
const IMAGE_SIZES = '(min-width: 1280px) 460px, (min-width: 768px) 40vw, 70vw';

/**
 * Figma „Gallery“ (60145:11472): main image with badges and arrows, thumbnails, lightbox. The image
 * list and the placeholder follow the core gallery; the carousel is embla (swipe, keyboard arrows on
 * the group, thumbnails as buttons), a click on the image opens the lightbox at that image.
 */
export function ProductGallery() {
  const { t } = useTranslations();
  const settings = useGlobalStore(s => s.templateSettings.product_detail);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const product = useVariantsStore(s => s.product);
  const selectedVariant = useVariantsStore(s => s.selectedVariant);

  const images = useMemo(
    () =>
      collectGalleryImages(
        settings?.gallery_with_image_variants?.enabled ?? false,
        product,
        selectedVariant,
      ),
    [settings?.gallery_with_image_variants?.enabled, product, selectedVariant],
  );
  const imagesKey = images.map(image => image.url).join('|');
  const multiple = images.length > 1;

  const price = getProductPriceDisplay(selectedVariant ?? product, {
    isWholesaler,
    priceWithoutVatFirst: settings?.price_without_vat_first?.enabled,
    discountDecimalPlaces: 0,
  });
  const badges = [
    ...(price.showPreviousPrice
      ? [{ key: 'sale', tone: 'sale' as const, label: `-${price.discountLabel} %` }]
      : []),
    ...(product.tags ?? []).map(tag => ({ key: tag.slug, tone: 'new' as const, label: tag.name })),
  ].slice(0, MAX_BADGES);

  // Embla needs more than two full-width slides to wrap around; with two the arrows stop at the ends.
  const loop = images.length > 2;
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop });
  const [selected, setSelected] = useState(0);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const viewed = useRef(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const refocus = useRef(false);

  useEffect(() => {
    if (!emblaApi) return;

    const update = () => setSelected(emblaApi.selectedScrollSnap());
    update();
    emblaApi.on('select', update).on('reInit', update);
    return () => {
      emblaApi.off('select', update).off('reInit', update);
    };
  }, [emblaApi]);

  // Slides that are not shown are inert: after an arrow key the focus follows to the new slide.
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    rootRef.current
      ?.querySelector<HTMLElement>(`${SLIDE}:not([inert]) button`)
      ?.focus({ preventScroll: true });
  }, [selected]);

  // Another variant brings another set of images: start from the first one.
  useEffect(() => {
    emblaApi?.scrollTo(0, true);
  }, [emblaApi, imagesKey]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const forward = event.key === 'ArrowRight';
    if (!emblaApi || !multiple || (!forward && event.key !== 'ArrowLeft')) return;

    event.preventDefault();
    if (!(forward ? emblaApi.canScrollNext() : emblaApi.canScrollPrev())) return;
    refocus.current = event.target instanceof Element && !!event.target.closest(SLIDE);
    if (forward) emblaApi.scrollNext();
    else emblaApi.scrollPrev();
  };

  const openLightbox = (index: number) => {
    viewed.current = index;
    setLightbox(index);
  };
  const closeLightbox = () => {
    emblaApi?.scrollTo(viewed.current, true);
    setLightbox(null);
  };

  return (
    <>
      <div
        ref={rootRef}
        data-section="product-gallery"
        role="group"
        aria-roledescription="carousel"
        aria-label={product.name}
        onKeyDown={onKeyDown}
        className="flex min-w-0 flex-col gap-3 self-start md:gap-4"
      >
        <div className="relative aspect-square overflow-hidden rounded-[18px] border border-[#E2E4E7] bg-white md:aspect-[652/620]">
          <div ref={emblaRef} className="absolute inset-0 overflow-hidden">
            <div className="flex h-full touch-pan-y touch-pinch-zoom">
              {images.length ? (
                images.map((image, index) => {
                  const alt = resolveDzlGalleryImageAlt(image, product.name);
                  return (
                    <div
                      key={`${image.id}-${index}`}
                      role="group"
                      aria-roledescription="slide"
                      aria-label={`${index + 1} / ${images.length}`}
                      inert={index !== selected}
                      className="relative h-full min-w-0 shrink-0 grow-0 basis-full"
                    >
                      <button
                        type="button"
                        onClick={() => openLightbox(index)}
                        aria-label={`${t('text3625')}: ${alt}`}
                        aria-haspopup="dialog"
                        className="absolute inset-x-[15%] inset-y-[9%] cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] md:inset-x-[14.7%] md:inset-y-[6.45%]"
                      >
                        <Image
                          src={image.url}
                          alt={alt}
                          fill
                          sizes={IMAGE_SIZES}
                          preload={index === 0}
                          fetchPriority={index === 0 ? 'high' : undefined}
                          className="object-contain"
                        />
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="relative h-full min-w-0 shrink-0 grow-0 basis-full">
                  <div className="absolute inset-x-[15%] inset-y-[9%] md:inset-x-[14.7%] md:inset-y-[6.45%]">
                    <Image
                      src={PLACEHOLDER}
                      alt={product.name}
                      fill
                      sizes={IMAGE_SIZES}
                      unoptimized
                      className="object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {badges.length ? (
            <div className="pointer-events-none absolute left-4 top-4 z-10 flex gap-1 md:left-6 md:top-6">
              {badges.map(badge => (
                <Badge key={badge.key} tone={badge.tone}>
                  {badge.label}
                </Badge>
              ))}
            </div>
          ) : null}

          {multiple ? (
            <>
              <IconButton
                icon="caret-left"
                iconSize={20}
                label="Predchádzajúci snímok"
                disabled={!loop && selected === 0}
                onClick={() => emblaApi?.scrollPrev()}
                className="absolute left-2 top-1/2 z-10 -translate-y-1/2 md:left-6 md:size-[52px]"
                size="s"
              />
              <IconButton
                icon="caret-right"
                iconSize={20}
                label={t('text3526')}
                disabled={!loop && selected === images.length - 1}
                onClick={() => emblaApi?.scrollNext()}
                className="absolute right-2 top-1/2 z-10 -translate-y-1/2 md:right-6 md:size-[52px]"
                size="s"
              />
            </>
          ) : null}
        </div>

        {multiple ? (
          <GalleryThumbnails
            images={images}
            selected={selected}
            onSelect={index => emblaApi?.scrollTo(index)}
          />
        ) : null}

        <p aria-live="polite" className="sr-only">
          {multiple ? `${selected + 1} / ${images.length}` : null}
        </p>
      </div>

      {lightbox !== null ? (
        <Suspense fallback={null}>
          <GalleryLightbox
            images={images}
            index={lightbox}
            alt={product.name}
            onView={index => {
              viewed.current = index;
            }}
            onClose={closeLightbox}
          />
        </Suspense>
      ) : null}
    </>
  );
}
